import {Button, FormControlLabel, Paper, Switch} from '@material-ui/core';
import {Loaded, toEither} from 'Core/models/Async';
import * as E from 'fp-ts/lib/Either';
import {constTrue, identity, not} from 'fp-ts/lib/function';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useRouteMatch} from 'react-router';
import {ReducerState} from 'rootReducer';
import {renderAsync} from 'Shared/components/RenderAsync';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import ValidatableEmailInput from 'Shared/components/ValidatableEmailInput';
import ValidatablePhoneInput from 'Shared/components/ValidatablePhoneInput';
import ValidatableTextInput from 'Shared/components/ValidatableTextInput';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {RecruiterCodeDto} from 'Users/api';
import {
  changeFieldSalesLeaderUser,
  clearFieldSalesLeader,
  fetchFieldSalesLeader,
  saveFieldSalesLeaderUser,
  validateEmail,
  validatePhone,
  validateUsername,
} from 'Users/ducks/fieldSalesLeaderUser';
import {
  eqFieldSalesLeader,
  ErrorFieldSalesLeader,
  FieldSalesLeader,
  ValidFieldSalesLeader,
} from 'Users/models/FieldSalesLeader';
import {UserRouteConstants} from 'Users/Routes';
import './AgentManualEntry.sass';

const FieldSalesLeaderEntry = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const fieldSalesLeaderUserId = O.fromNullable(useRouteMatch<{userId?: string}>().params.userId);
  const isEdit = O.isSome(fieldSalesLeaderUserId);

  const [recruiterCode] = useState<RecruiterCodeDto>();
  const {fieldSalesLeader, originalFieldSalesLeader} = useSelector(
    (state: ReducerState) => state.user.fieldSalesLeaderUser
  );

  const fieldSalesLeaderLens = Lens.fromProp<FieldSalesLeader>();

  useEffect(() => {
    pipe(
      fieldSalesLeaderUserId,
      O.fold(NoOp, userId => dispatch(fetchFieldSalesLeader(userId)))
    );

    return () => {
      dispatch(clearFieldSalesLeader());
    };
  }, []);

  const changeFieldSalesLeader = (fieldSalesLeaderUser: FieldSalesLeader) =>
    dispatch(
      changeFieldSalesLeaderUser(
        new Loaded(
          ValidFieldSalesLeader.create(
            fieldSalesLeaderUser.firstName,
            fieldSalesLeaderUser.lastName,
            fieldSalesLeaderUser.username,
            fieldSalesLeaderUser.phoneNumber,
            fieldSalesLeaderUser.email,
            fieldSalesLeaderUser.recruiterCode,
            fieldSalesLeaderUser.isNewYork,
            fieldSalesLeaderUser.isAhl
          )
        )
      )
    );

  const renderFSLForm = (fsl: FieldSalesLeader) => (original: O.Option<FieldSalesLeader>) => {
    const equalsCurrentFsl = (otherFsl: FieldSalesLeader) => eqFieldSalesLeader.equals(fsl, otherFsl);
    const hasFslChanged = pipe(original, O.fold(constTrue, not(equalsCurrentFsl)));
    const allowSave = !(fsl instanceof ValidFieldSalesLeader) || !hasFslChanged;
    const methodLabel = pipe(
      fieldSalesLeaderUserId,
      O.fold(
        () => 'Add',
        () => 'Edit'
      )
    );

    return (
      <Paper className="agent-entry">
        <h2 className="agent-entry__title">{`${methodLabel} Field Sales Leader`}</h2>
        <p className="agent-entry__description">Please enter the Field Sales Leader user's information below.</p>
        <form className="agent-entry__form">
          <RequiredTextInput
            label="First Name"
            className="agent-entry__field"
            onChange={firstName => changeFieldSalesLeader(fieldSalesLeaderLens('firstName').set(firstName)(fsl))}
            value={fsl.firstName}
          />
          <RequiredTextInput
            label="Last Name"
            className="agent-entry__field"
            onChange={lastName => changeFieldSalesLeader(fieldSalesLeaderLens('lastName').set(lastName)(fsl))}
            value={fsl.lastName}
          />
          <ValidatableTextInput
            label="User Name"
            disabled={isEdit}
            className="agent-entry__field"
            onValidate={userName => dispatch(validateUsername(userName))}
            value={fsl.username}
          />
          <ValidatableEmailInput
            label="Email"
            className="agent-entry__field"
            onValidate={email => dispatch(validateEmail(email))}
            value={fsl.email}
          />
          <ValidatablePhoneInput
            label="Phone Number"
            onValidate={phoneNumber => dispatch(validatePhone(phoneNumber))}
            className="agent-entry__field"
            value={fsl.phoneNumber}
          />
          {recruiterCode && recruiterCode.isAhl && recruiterCode.isNewYork && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={fsl.isAhl}
                    onChange={event => {
                      changeFieldSalesLeader(fieldSalesLeaderLens('isAhl').set(event.target.checked)(fsl));
                    }}
                  />
                }
                label="American Heritage Life Access?"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={fsl.isNewYork}
                    onChange={event => {
                      changeFieldSalesLeader(fieldSalesLeaderLens('isNewYork').set(event.target.checked)(fsl));
                    }}
                  />
                }
                label="New York Access?"
              />
            </>
          )}
          <div className="agent-entry-form__buttons">
            <div className="agent-entry-form__return-button">
              <Button
                variant="contained"
                onClick={() => {
                  history.push(UserRouteConstants.USERS);
                }}
              >
                Cancel
              </Button>
            </div>
            <Button
              className="agent-entry-form__add-button"
              color="primary"
              variant="contained"
              disabled={allowSave}
              onClick={() =>
                dispatch(
                  saveFieldSalesLeaderUser(
                    fsl as ValidFieldSalesLeader,
                    pipe(
                      fieldSalesLeaderUserId,
                      O.fold(() => '', identity)
                    )
                  )
                )
              }
            >
              Save
            </Button>
          </div>
          <p className="agent-entry-form__error-message">
            {fsl instanceof ErrorFieldSalesLeader && fsl.errors.message}
          </p>
        </form>
      </Paper>
    );
  };

  const eitherFsl = toEither(fieldSalesLeader);
  const originalFslOption = pipe(
    originalFieldSalesLeader,
    O.fold(
      () => O.none,
      original => O.fromEither(toEither(original))
    )
  );

  return pipe(E.right(renderFSLForm), E.ap(eitherFsl), E.ap(E.right(originalFslOption)), E.fold(renderAsync, identity));
};

export default FieldSalesLeaderEntry;
